import React from 'react';
import i18n from '@/i18n/config';
import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { apiClient } from '@/utils/api';
import { useAtom } from 'jotai';
import { addOrganizationDomain } from '@/store';

interface AddDomainFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  onSuccess: () => void;
}

interface DomainFormData {
  title: string;
  domain: string;
  tokenLimit: string;
}

export function AddDomainForm({ isOpen, onOpenChange, organizationId, onSuccess }: AddDomainFormProps) {
  const [formData, setFormData] = React.useState<DomainFormData>({
    title: '',
    domain: '',
    tokenLimit: ''
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [, addOrganizationDomainAtom] = useAtom(addOrganizationDomain);

  const handleSubmit = React.useCallback(async () => {
    if (!formData.title.trim() || !formData.domain.trim()) return;

    try {
      setIsSubmitting(true);
      const parsed = formData.tokenLimit.trim()
        ? parseInt(formData.tokenLimit, 10)
        : null;
      const token_limit =
        parsed != null && !Number.isNaN(parsed) && parsed >= 1 ? parsed : null;
      await addOrganizationDomainAtom({
        organizationId,
        domain: formData.domain.trim(),
        title: formData.title.trim(),
        token_limit
      });

      // Reset form
      setFormData({
        title: '',
        domain: '',
        tokenLimit: ''
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      // Error handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, organizationId, onSuccess, onOpenChange, addOrganizationDomainAtom]);

  const handleClose = React.useCallback(() => {
    setFormData({
      title: '',
      domain: '',
      tokenLimit: ''
    });
    onOpenChange(false);
  }, [onOpenChange]);

  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isSubmitting && formData.title.trim() && formData.domain.trim()) {
        handleSubmit();
      }
    },
    [handleSubmit, isSubmitting, formData.title, formData.domain]
  );

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Add Domain</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4" onKeyDown={handleKeyPress}>
                <Input
                  label={i18n.t('columns.title')}
                  placeholder={i18n.t('copy.enter_domain_title')}
                  value={formData.title}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, title: value }))}
                  isRequired
                />
                <Input
                  label={i18n.t('columns.domain')}
                  placeholder={i18n.t('copy.enter_domain_e_g_example_com')}
                  value={formData.domain}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, domain: value }))}
                  isRequired
                />
                <Input
                  type="number"
                  min={1}
                  label={i18n.t('copy.domain_event_cap')}
                  placeholder={i18n.t('copy.leave_empty_for_unlimited')}
                  value={formData.tokenLimit}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, tokenLimit: value }))}
                  description="Max events processed for this domain. Leave empty for unlimited."
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button
                color="primary"
                isLoading={isSubmitting}
                onPress={handleSubmit}
                isDisabled={!formData.title.trim() || !formData.domain.trim()}
              >
                Add Domain
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
